/**
 * useBooking Hook
 * 
 * Hook to access booking context.
 * Must be used within BookingProvider.
 * 
 * @module booking/context
 */

import { useContext } from 'react';
import { BookingContext } from './BookingContext';
import type { BookingContextValue } from '../types';

/**
 * Hook to access booking context
 * 
 * @throws {Error} If used outside BookingProvider
 * 
 * @example
 * ```tsx
 * function BaySelector() {
 *   const { selectedBayId, setSelectedBayId } = useBooking();
 * 
 *   return (
 *     <div>
 *       <h2>Select a Bay</h2>
 *       {bays.map(bay => (
 *         <button
 *           key={bay.id}
 *           onClick={() => setSelectedBayId(bay.id)}
 *           className={selectedBayId === bay.id ? 'selected' : ''}
 *         >
 *           Bay {bay.id}
 *         </button>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useBooking(): BookingContextValue {
  const context = useContext(BookingContext);
  
  if (!context) {
    throw new Error(
      'useBooking must be used within BookingProvider. ' +
      'Did you forget to wrap your component with <BookingProvider>?'
    );
  }
  
  return context;
}
